// Componente de Registo e Validação de Vales
import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { 
  CreditCard, Ticket, CheckCircle, AlertCircle, 
  AlertTriangle, Clock, Search, Plus, Scan
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const RegistoVales = ({ user, modo = 'registar', contingenciaAtiva = false }) => {
  const [idCartao, setIdCartao] = useState('');
  const [clienteData, setClienteData] = useState(null);
  const [parcerias, setParcerias] = useState([]);
  const [parceriaSelecionada, setParceriaSelecionada] = useState('');
  const [valorCompra, setValorCompra] = useState('');
  const [litrosAbastecidos, setLitrosAbastecidos] = useState('');
  const [numeroFatura, setNumeroFatura] = useState('');
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [modoContingencia, setModoContingencia] = useState(false);
  const [numeroValeManual, setNumeroValeManual] = useState('');
  const [emitidoPor, setEmitidoPor] = useState('');
  const [usadoEm, setUsadoEm] = useState('');
  const [valeSelecionado, setValeSelecionado] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [litrosValidacao, setLitrosValidacao] = useState('');
  const [valorCompraParceiro, setValorCompraParceiro] = useState('');
  const [numeroFaturaValidacao, setNumeroFaturaValidacao] = useState('');
  
  const inputRef = useRef(null);

  const [parceriaAtiva, setParceriaAtiva] = useState(true);

  const isJaneira = ['admin', 'responsavel', 'colaborador'].includes(user?.role);
  const isParceria = ['admin_parceria', 'colaborador_parceria'].includes(user?.role);

  useEffect(() => {
    if (isJaneira) {
      setEmitidoPor('Janeira');
      loadParcerias();
    } else if (isParceria) {
      loadParceriaPropria();
    }
    inputRef.current?.focus();
  }, []);

  const loadParcerias = async () => {
    try {
      const response = await api.get('/parcerias/entidades?estado=true');
      setParcerias(response.data);
    } catch (error) {
      console.error('Erro ao carregar parcerias:', error);
    }
  };

  const loadParceriaPropria = async () => {
    try {
      const response = await api.get('/parcerias/entidades');
      if (response.data.length > 0) {
        const p = response.data[0];
        // Verificar se a parceria está ativa
        const estaAtiva = p.estado !== false;
        setParceriaAtiva(estaAtiva);
        
        if (estaAtiva) {
          setParcerias([{ id: p.id, nome: p.nome_entidade }]);
          setParceriaSelecionada(p.id);
          setEmitidoPor(p.nome_entidade);
          setUsadoEm('Janeira');
        }
      }
    } catch (error) {
      console.error('Erro ao carregar parceria:', error);
    }
  };

  // Selecionar parceria - "Usado Em" atualiza com o nome da parceria para Janeira users
  const handleSelectParceria = (parceriaId) => {
    setParceriaSelecionada(parceriaId);
    if (isJaneira) {
      const parceria = parcerias.find(p => p.id === parceriaId);
      if (parceria) {
        setUsadoEm(parceria.nome || parceria.nome_entidade);
      }
    }
  };

  const handleLerCartao = async () => {
    if (!idCartao.trim()) {
      toast.error('Introduza o ID do cartão');
      return;
    }

    setSearching(true);
    setClienteData(null);
    setValeSelecionado(null);

    try {
      const response = await api.post('/parcerias/rfid/ler', null, {
        params: { id_cartao: idCartao }
      });

      if (response.data.encontrado) {
        if (response.data.ativo) {
          setClienteData(response.data);
          if (response.data.parcerias_disponiveis?.length > 0) {
            setParcerias(response.data.parcerias_disponiveis);
          }
          toast.success(`Cliente encontrado: ${response.data.cliente?.nome_cliente || 'Sem nome'}`);
        } else {
          toast.error('Este cartão está inativo');
        }
      } else {
        toast.warning(response.data.mensagem || 'Cartão não encontrado');
        if (response.data.pode_registar) {
          // Sugerir registar novo cliente
        }
      }
    } catch (error) {
      console.error('Erro ao ler cartão:', error);
      toast.error('Erro ao ler cartão');
    } finally {
      setSearching(false);
    }
  };

  const handleRegistarVale = async () => {
    if (!modoContingencia && !idCartao) {
      toast.error('Aproxime o cartão do cliente');
      return;
    }

    if (!parceriaSelecionada) {
      toast.error('Selecione uma parceria');
      return;
    }

    if (!valorCompra || parseFloat(valorCompra) <= 0) {
      toast.error('Introduza o valor da compra');
      return;
    }

    if (!numeroFatura.trim()) {
      toast.error('Introduza o número da fatura');
      return;
    }

    if (modoContingencia && !numeroValeManual.trim()) {
      toast.error('Introduza o número do vale manual');
      return;
    }

    setShowConfirmDialog(true);
  };

  const confirmarRegisto = async () => {
    setShowConfirmDialog(false);
    setLoading(true);

    try {
      const valeData = {
        entidade_parceira_id: parceriaSelecionada,
        emitido_por: emitidoPor,
        usado_em: usadoEm,
        numero_fatura: numeroFatura,
        valor_compra: parseFloat(valorCompra),
        ...(litrosAbastecidos && { litros_abastecidos: parseFloat(litrosAbastecidos) }),
        ...(modoContingencia 
          ? { numero_vale_manual: numeroValeManual }
          : { id_cartao_rfid: idCartao }
        )
      };

      const response = await api.post('/parcerias/vales/registar', valeData);
      
      toast.success(
        <div>
          <p className="font-semibold">Vale registado com sucesso!</p>
          <p className="text-sm">Número: {response.data.numero_vale}</p>
          <p className="text-sm">Desconto: €{response.data.valor_desconto_aplicado?.toFixed(2)}</p>
          <p className="text-sm">Válido até: {new Date(response.data.data_validade).toLocaleDateString('pt-PT')}</p>
        </div>
      );

      // Limpar formulário
      resetForm();

    } catch (error) {
      console.error('Erro ao registar vale:', error);
      toast.error(error.response?.data?.detail || 'Erro ao registar vale');
    } finally {
      setLoading(false);
    }
  };

  const handleValidarVale = async () => {
    if (!valeSelecionado) {
      toast.error('Selecione um vale para validar');
      return;
    }

    if (!numeroFaturaValidacao.trim()) {
      toast.error('Introduza o número da fatura');
      return;
    }

    const regra = valeSelecionado.regra_aplicada;
    const tipo = regra?.tipo_beneficio;
    const precisaLitros = tipo === '€/Litro';
    const precisaValor = tipo === 'Valor Fixo' || tipo === 'Percentagem';

    if (precisaLitros && (!litrosValidacao || parseFloat(litrosValidacao) <= 0)) {
      toast.error('Introduza os litros abastecidos');
      return;
    }
    if (precisaValor && (!valorCompraParceiro || parseFloat(valorCompraParceiro) <= 0)) {
      toast.error('Introduza o valor da compra do cliente');
      return;
    }

    setLoading(true);

    try {
      const body = {
        numero_fatura: numeroFaturaValidacao.trim()
      };
      if (precisaLitros) {
        body.litros_abastecidos = parseFloat(litrosValidacao);
      }
      if (precisaValor) {
        body.valor_compra_parceiro = parseFloat(valorCompraParceiro);
      }
      const response = await api.post(`/parcerias/vales/${valeSelecionado.id}/validar`, body);
      
      const desconto = response.data.valor_desconto_aplicado?.toFixed(2);
      toast.success(
        <div>
          <p className="font-semibold">Vale validado com sucesso!</p>
          <p className="text-sm">Desconto aplicado: €{desconto}</p>
        </div>
      );

      resetForm();

    } catch (error) {
      console.error('Erro ao validar vale:', error);
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map(d => d.msg).join(', '));
      } else {
        toast.error(detail || 'Erro ao validar vale');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setIdCartao('');
    setClienteData(null);
    if (isJaneira) {
      setParceriaSelecionada('');
      setUsadoEm('');
      loadParcerias();
    }
    setValorCompra('');
    setLitrosAbastecidos('');
    setNumeroFatura('');
    setNumeroValeManual('');
    setValeSelecionado(null);
    setLitrosValidacao('');
    setValorCompraParceiro('');
    setNumeroFaturaValidacao('');
    setModoContingencia(false);
    inputRef.current?.focus();
  };

  const parceriaSelecionadaData = parcerias.find(p => p.id === parceriaSelecionada);
  const parceriaNome = parceriaSelecionadaData?.nome || parceriaSelecionadaData?.nome_entidade || '';
  const isPosto = parceriaSelecionadaData?.tipo_entidade === 'Posto de Combustível' ||
                  parceriaNome.toLowerCase().includes('posto') || 
                  parceriaNome.toLowerCase().includes('combustível');

  return (
    <div className="space-y-4" data-testid={`vales-${modo}`}>
      {/* Alerta de Contingência */}
      {contingenciaAtiva && (
        <Alert variant="warning" className="bg-orange-50 border-orange-200">
          <AlertTriangle className="h-4 w-4 text-orange-600" />
          <AlertDescription className="text-orange-700">
            Modo de contingência ativo. Pode registar vales manualmente se necessário.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {modo === 'registar' ? (
              <>
                <Plus className="h-5 w-5" />
                Registar Novo Vale
              </>
            ) : (
              <>
                <CheckCircle className="h-5 w-5" />
                Validar Vale
              </>
            )}
          </CardTitle>
          <CardDescription>
            {modo === 'registar' 
              ? 'Aproxime o cartão do cliente para registar um novo vale'
              : 'Aproxime o cartão do cliente para validar um vale'
            }
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Toggle Contingência */}
          {contingenciaAtiva && modo === 'registar' && (
            <div className="flex items-center gap-4 p-3 bg-muted rounded-lg">
              <input
                type="checkbox"
                id="modoContingencia"
                checked={modoContingencia}
                onChange={(e) => setModoContingencia(e.target.checked)}
                className="h-4 w-4"
              />
              <Label htmlFor="modoContingencia" className="cursor-pointer">
                Usar modo de contingência (vale manual)
              </Label>
            </div>
          )}

          {/* Input Pesquisa Cliente */}
          {!modoContingencia && (
            <div className="space-y-2">
              <Label>Pesquisar Cliente</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    ref={inputRef}
                    placeholder="Cartao RFID, nome, NIF ou telefone..."
                    value={idCartao}
                    onChange={(e) => setIdCartao(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleLerCartao()}
                    className="pl-10"
                    data-testid="input-rfid"
                  />
                </div>
                <Button 
                  onClick={handleLerCartao} 
                  disabled={searching}
                  data-testid="btn-ler-cartao"
                >
                  {searching ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  ) : (
                    <>
                      <Scan className="h-4 w-4 mr-2" />
                      Ler
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Dados do Cliente */}
          {clienteData && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="font-semibold text-green-700">Cliente Identificado</span>
                </div>
                <Badge variant="outline">{clienteData.cliente?.id_cartao_rfid}</Badge>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Nome:</span>
                  <p className="font-medium">{clienteData.cliente?.nome_cliente || 'Não definido'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Telefone:</span>
                  <p className="font-medium">{clienteData.cliente?.telefone || '-'}</p>
                </div>
              </div>

              {/* Vales Ativos */}
              {(() => {
                // Em modo validar, filtrar vales pela direção correta
                let valesVisiveis = clienteData.vales_ativos || [];
                if (modo === 'validar') {
                  if (isJaneira) {
                    // Janeira só valida vales "parceiro_janeira" (usados no Janeira)
                    valesVisiveis = valesVisiveis.filter(v => v.direcao === 'parceiro_janeira');
                  } else if (isParceria) {
                    // Parceiro só valida vales "janeira_parceiro" (usados no parceiro)
                    valesVisiveis = valesVisiveis.filter(v => v.direcao === 'janeira_parceiro');
                  }
                }
                return valesVisiveis.length > 0 ? (
                <div className="mt-3 pt-3 border-t border-green-200">
                  <p className="text-sm font-medium text-green-700 mb-2">
                    {modo === 'validar' ? `Vales para Validar (${valesVisiveis.length})` : `Vales Ativos (${clienteData.total_vales_ativos})`}
                  </p>
                  <div className="space-y-2">
                    {valesVisiveis.map(vale => (
                      <div 
                        key={vale.id}
                        className={`p-2 rounded border cursor-pointer transition-colors ${
                          valeSelecionado?.id === vale.id 
                            ? 'bg-green-100 border-green-400' 
                            : 'bg-white border-gray-200 hover:border-green-300'
                        }`}
                        onClick={() => modo === 'validar' && setValeSelecionado(vale)}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-sm">{vale.numero_vale}</span>
                          <Badge variant="success" className="bg-green-100 text-green-700">
                            €{vale.valor_desconto_aplicado?.toFixed(2)}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                          <Clock className="h-3 w-3" />
                          Válido até: {new Date(vale.data_validade).toLocaleDateString('pt-PT')}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null;
              })()}

              {(() => {
                let valesVisiveis = clienteData.vales_ativos || [];
                if (modo === 'validar') {
                  if (isJaneira) {
                    valesVisiveis = valesVisiveis.filter(v => v.direcao === 'parceiro_janeira');
                  } else if (isParceria) {
                    valesVisiveis = valesVisiveis.filter(v => v.direcao === 'janeira_parceiro');
                  }
                }
                return valesVisiveis.length === 0 && modo === 'validar' ? (
                <Alert className="mt-3">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Este cliente não tem vales para validar aqui.
                  </AlertDescription>
                </Alert>
              ) : null;
              })()}
            </div>
          )}

          {/* Aviso se parceria inativa (só para parceiros) */}
          {modo === 'registar' && isParceria && !parceriaAtiva && clienteData && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center gap-2 text-amber-800">
                <AlertTriangle className="h-5 w-5" />
                <p className="font-medium">Parceria Inativa</p>
              </div>
              <p className="text-sm text-amber-700 mt-1">
                A parceria está atualmente inativa. Não é possível registar novos vales.
                Contacte o administrador do Janeira.
              </p>
            </div>
          )}

          {/* Formulário de Registo - só mostra se parceria ativa (ou se é Janeira) */}
          {modo === 'registar' && (clienteData || modoContingencia) && (isJaneira || parceriaAtiva) && (
            <div className="space-y-4 pt-4 border-t">
              {/* Número Vale Manual (Contingência) */}
              {modoContingencia && (
                <div className="space-y-2">
                  <Label>Número do Vale (escrito no papel)</Label>
                  <Input
                    placeholder="Ex: VALE-12345"
                    value={numeroValeManual}
                    onChange={(e) => setNumeroValeManual(e.target.value)}
                    data-testid="input-vale-manual"
                  />
                </div>
              )}

              {/* Parceria */}
              <div className="space-y-2">
                <Label>Parceria</Label>
                <Select value={parceriaSelecionada} onValueChange={handleSelectParceria}>
                  <SelectTrigger data-testid="select-parceria">
                    <SelectValue placeholder="Selecione a parceria..." />
                  </SelectTrigger>
                  <SelectContent>
                    {parcerias.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.nome || p.nome_entidade}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Emitido Por / Usado Em - Automático, só mostra após selecionar parceria */}
              {parceriaSelecionada && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Emitido Por</Label>
                    <div className="px-3 py-2 bg-muted rounded-md text-sm font-medium" data-testid="campo-emitido-por">{emitidoPor}</div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Usado Em</Label>
                    <div className="px-3 py-2 bg-muted rounded-md text-sm font-medium" data-testid="campo-usado-em">{usadoEm}</div>
                  </div>
                </div>
              )}

              {/* Valor da Compra */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Valor da Compra (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={valorCompra}
                    onChange={(e) => setValorCompra(e.target.value)}
                    data-testid="input-valor"
                  />
                </div>

                {/* Litros (se combustível) */}
                {isPosto && (
                  <div className="space-y-2">
                    <Label>Litros Abastecidos</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={litrosAbastecidos}
                      onChange={(e) => setLitrosAbastecidos(e.target.value)}
                    />
                  </div>
                )}
              </div>

              {/* Número da Fatura */}
              <div className="space-y-2">
                <Label>Número da Fatura</Label>
                <Input
                  placeholder="Ex: FT 2026/12345"
                  value={numeroFatura}
                  onChange={(e) => setNumeroFatura(e.target.value)}
                  data-testid="input-fatura"
                />
              </div>

              {/* Botão Registar */}
              <Button 
                onClick={handleRegistarVale} 
                disabled={loading}
                className="w-full"
                size="lg"
                data-testid="btn-registar"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                ) : (
                  <>
                    <Ticket className="h-5 w-5 mr-2" />
                    Registar Vale
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Campo Litros/Valor + Fatura + Botão Validar */}
          {modo === 'validar' && valeSelecionado && (
            <div className="space-y-4">
              {/* Campo de litros se regra é €/Litro */}
              {valeSelecionado.regra_aplicada?.tipo_beneficio === '€/Litro' && (
                <div className="space-y-2 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <Label className="text-amber-800 font-medium">Litros Abastecidos</Label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="Ex: 15"
                    value={litrosValidacao}
                    onChange={(e) => setLitrosValidacao(e.target.value)}
                    data-testid="input-litros-validacao"
                    className="text-lg"
                  />
                  {litrosValidacao && parseFloat(litrosValidacao) > 0 && (
                    <div className="text-sm text-amber-700">
                      <span className="font-medium">Desconto: </span>
                      €{(Math.min(
                        parseFloat(litrosValidacao),
                        valeSelecionado.regra_aplicada?.limite_litros || 999999
                      ) * (valeSelecionado.regra_aplicada?.valor_beneficio || 0)).toFixed(2)}
                      <span className="text-amber-500 ml-1">
                        ({Math.min(parseFloat(litrosValidacao), valeSelecionado.regra_aplicada?.limite_litros || 999999).toFixed(1)}L × €{valeSelecionado.regra_aplicada?.valor_beneficio}/L)
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Campo de valor compra para Valor Fixo / Percentagem */}
              {(valeSelecionado.regra_aplicada?.tipo_beneficio === 'Valor Fixo' || 
                valeSelecionado.regra_aplicada?.tipo_beneficio === 'Percentagem') && (
                <div className="space-y-2 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <Label className="text-blue-800 font-medium">Valor da Compra do Cliente (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder={`Mín: €${valeSelecionado.regra_aplicada?.valor_minimo_compra || 0}`}
                    value={valorCompraParceiro}
                    onChange={(e) => setValorCompraParceiro(e.target.value)}
                    data-testid="input-valor-parceiro"
                    className="text-lg"
                  />
                  {valorCompraParceiro && parseFloat(valorCompraParceiro) > 0 && (
                    <div className="text-sm">
                      {parseFloat(valorCompraParceiro) >= (valeSelecionado.regra_aplicada?.valor_minimo_compra || 0) ? (
                        <span className="text-green-700 font-medium">
                          Desconto: €{valeSelecionado.regra_aplicada?.tipo_beneficio === 'Percentagem' 
                            ? (parseFloat(valorCompraParceiro) * (valeSelecionado.regra_aplicada?.valor_beneficio || 0) / 100).toFixed(2)
                            : valeSelecionado.regra_aplicada?.valor_beneficio?.toFixed(2)
                          }
                          {valeSelecionado.regra_aplicada?.tipo_beneficio === 'Percentagem' && (
                            <span className="text-green-500 ml-1">({valeSelecionado.regra_aplicada?.valor_beneficio}%)</span>
                          )}
                        </span>
                      ) : (
                        <span className="text-red-600">
                          Valor mínimo: €{valeSelecionado.regra_aplicada?.valor_minimo_compra}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Número da Fatura - obrigatório na validação */}
              <div className="space-y-2">
                <Label>Número da Fatura *</Label>
                <Input
                  placeholder="Ex: FT 2026/12345"
                  value={numeroFaturaValidacao}
                  onChange={(e) => setNumeroFaturaValidacao(e.target.value)}
                  data-testid="input-fatura-validacao"
                />
              </div>

              <Button 
                onClick={handleValidarVale} 
                disabled={loading}
                className="w-full"
                size="lg"
                data-testid="btn-validar"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                ) : (
                  <>
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Validar Vale
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog de Confirmação */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Registo de Vale</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Parceria:</span>
              <span className="font-medium">{parceriaNome}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Emitido Por:</span>
              <span className="font-medium">{emitidoPor}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Usado Em:</span>
              <span className="font-medium">{usadoEm}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Valor da Compra:</span>
              <span className="font-medium">€{parseFloat(valorCompra || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Fatura:</span>
              <span className="font-medium">{numeroFatura}</span>
            </div>
            {modoContingencia && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Vale Manual:</span>
                <span className="font-medium">{numeroValeManual}</span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={confirmarRegisto} disabled={loading}>
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RegistoVales;
